import React, { useState, useRef, memo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import 'katex/dist/katex.min.css';
import axios from 'axios';
import { toast } from 'react-toastify';
import katex from 'katex';
import { API_BASE_URL } from '../../config';

window.katex = katex;

// Minimal Image Resize Module for Quill


// Add global styles for inline images in Quill and rendered output
const inlineImageStyle = `
  .ql-editor img,
  .prose img {
    display: inline-block !important;
    vertical-align: middle;
    margin: 0 8px;
    max-width: 100%;
    filter: invert(1) hue-rotate(180deg) brightness(1.1);
    mix-blend-mode: screen;
  }
`;

// Symbol Dropdown Component
const SymbolPicker = memo(({ onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const symbols = [
    'α', 'β', 'γ', 'δ', 'ε', 'ζ', 'η', 'θ', 'ι', 'κ', 'λ', 'μ', 'ν', 'ξ', 'ο', 'π', 'ρ', 'σ', 'τ', 'υ', 'φ', 'χ', 'ψ', 'ω',
    'Γ', 'Δ', 'Θ', 'Λ', 'Ξ', 'Π', 'Σ', 'Φ', 'Ψ', 'Ω',
    '±', '÷', '×', '√', '∞', '≈', '≠', '≤', '≥', '∴', '∵', '∫', '∂', '∆', '∇', '∑', '∏', '∩', '∪', '°', 'Å', 'λ'
  ];

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-t-lg border-x border-t border-slate-700 transition-colors"
      >
        <span className="text-blue-400">Ω</span> Symbols
        <svg className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 bottom-full z-[70] mb-0 w-64 p-3 bg-slate-900 border border-slate-700 rounded-xl rounded-bl-none shadow-2xl animate-in fade-in slide-in-from-bottom-2">
            <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
              {symbols.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    onSelect(s);
                    // Keep open for multi-selection, or close? 
                    // Let's keep it open but provide a close button or click-outside
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded bg-slate-800 hover:bg-blue-500 hover:text-white text-slate-300 transition-colors text-sm font-medium"
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-slate-800 flex justify-end">
              <button 
                type="button" 
                onClick={() => setIsOpen(false)}
                className="text-[10px] text-slate-500 hover:text-slate-300 uppercase tracking-widest font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const HtmlEditor = memo(({
  editorId,
  value,
  onChange,
  onFocus,
  registerRef,
  placeholder,
  minHeightClass = 'min-h-[120px]',
  paddingClass = 'p-4',
  textClassName = 'text-base'
}) => {
  const editorRef = useRef(null);
  const [resizingImg, setResizingImg] = React.useState(null);

  React.useEffect(() => {
    const el = editorRef.current;
    const nextValue = value || '';

    if (el && el.innerHTML !== nextValue) {
      el.innerHTML = nextValue;
    }
  }, [value]);

  const setRefs = useCallback((el) => {
    editorRef.current = el;
    if (registerRef) {
      registerRef(el);
    }
  }, [registerRef]);

  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        const formData = new FormData();
        formData.append('file', blob);
        formData.append('title', 'Pasted Image');
        formData.append('subject', 'General');
        formData.append('type', 'Image');

        try {
          const res = await axios.post(`${API_BASE_URL}/api/files/upload`, formData);
          const imageUrl = `${API_BASE_URL}/api/files/view/${res.data.id}`;
          const imgHtml = `<img src="${imageUrl}" style="max-width: 100%; cursor: nwse-resize; display: inline-block; vertical-align: middle; margin: 0 4px;" />`;
          document.execCommand('insertHTML', false, imgHtml);
          if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        } catch (err) {
          console.error("Paste upload failed", err);
          toast.error("Failed to upload pasted image");
        }
      }
    }
    // If not an image, let default paste happen (but we might want to clean HTML later)
  };

    const handleMouseDown = (e) => {
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
      const rect = e.target.getBoundingClientRect();
      setResizingImg({
        el: e.target,
        startWidth: rect.width,
        startX: e.clientX
      });
      // Visual feedback
      e.target.style.outline = "2px solid #3b82f6";
      e.target.style.opacity = "0.8";
    }
  };

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (resizingImg) {
        const deltaX = e.clientX - resizingImg.startX;
        const newWidth = Math.max(50, resizingImg.startWidth + deltaX);
        resizingImg.el.style.width = `${newWidth}px`;
        resizingImg.el.style.height = 'auto'; // Aspect ratio 
      }
    };

    const handleMouseUp = () => {
      if (resizingImg) {
        resizingImg.el.style.outline = "none";
        resizingImg.el.style.opacity = "1";
        if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        setResizingImg(null);
      }
    };

    if (resizingImg) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingImg, onChange]);

  return (
    <div className="relative">
      {!value && placeholder && (
        <div className={`pointer-events-none absolute left-4 top-4 z-10 text-slate-500 ${textClassName}`}>
          {placeholder}
        </div>
      )}
      <div
        ref={setRefs}
        data-editor-id={editorId}
        contentEditable
        suppressContentEditableWarning
        onFocus={onFocus}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onPaste={handlePaste}
        onMouseDown={handleMouseDown}
        onDragStart={(e) => { if (e.target.tagName === 'IMG') e.preventDefault(); }}
        className={`w-full bg-transparent ${paddingClass} ${minHeightClass} ${textClassName} text-slate-200 outline-none overflow-x-auto whitespace-pre-wrap break-words [&_p]:my-0 [&_img]:inline-block [&_img]:align-middle [&_img]:max-w-full [&_img]:cursor-nwse-resize [&_img]:transition-opacity`}
      />
    </div>
  );
});

// Memoized Question Item to prevent list re-renders
const QuestionItem = memo(({ q, onEdit, onDelete, renderRichText }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="p-5 rounded-2xl border border-slate-800 bg-slate-900/40 hover:border-slate-700 transition-all duration-300">
      <div className="flex items-start justify-between mb-3">
        <div className="flex flex-wrap gap-2 text-[10px] font-semibold uppercase tracking-wider">
          <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{q.subject}</span>
          {q.chapter && <span className="px-2 py-1 rounded bg-slate-800 text-slate-400">{q.chapter}</span>}
          <span className="px-2 py-1 rounded bg-slate-800 text-slate-400">Diff: {q.difficulty}</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => onEdit(q)} className="text-slate-500 hover:text-blue-400 transition-colors p-1" title="Edit Question">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
          </button>
          <button onClick={() => onDelete(q.id)} className="text-slate-500 hover:text-rose-400 transition-colors p-1" title="Delete Question">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
          </button>
        </div>
      </div>
      <div className="text-slate-200 font-medium mb-4">
        <div 
          className="prose prose-invert max-w-none prose-sm [&_p]:m-0"
          dangerouslySetInnerHTML={renderRichText(q.text)} 
        />
      </div>
      {q.questionType === 'Integer Type' ? (
        <div className="mt-4 p-5 rounded-2xl border border-dashed border-slate-800 bg-slate-950/20 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Correct Answer</span>
            <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 font-mono font-bold text-lg shadow-inner ring-1 ring-emerald-500/20">
              {q.correctOption}
            </div>
          </div>
          <div className="h-12 w-full rounded-xl bg-slate-950/40 border border-slate-800/50 flex items-center justify-center">
            <span className="text-slate-600 text-xs italic tracking-tighter">Student Answer Field Placeholder</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
          {q.options.map((opt, idx) => (
            <div key={idx} className={`p-3 rounded-xl border text-sm flex items-center gap-2 ${
              (q.questionType === 'Multiple Correct' ? q.correctOption?.includes(String.fromCharCode(65 + idx)) : q.correctOptionIndex === idx)
                ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200' 
                : 'border-slate-800 bg-slate-950/50 text-slate-400'
            }`}>
              <span className="font-medium opacity-60 w-5">{String.fromCharCode(65 + idx)}.</span> 
              <span 
                className="flex-1 overflow-x-auto hide-scrollbar prose prose-invert prose-sm [&_p]:inline"
                dangerouslySetInnerHTML={renderRichText(opt)}
              />
              {(q.questionType === 'Multiple Correct' ? q.correctOption?.includes(String.fromCharCode(65 + idx)) : q.correctOptionIndex === idx) && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500"><polyline points="20 6 9 17 4 12"></polyline></svg>
              )}
            </div>
          ))}
        </div>
      )}
      {(q.topic || q.customTags || q.questionType) && (
        <div className="mt-4 pt-4 border-t border-slate-800/60 flex flex-wrap gap-3 items-center">
          {q.questionType && (
            <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 px-2 py-1 rounded-md border border-blue-500/20 uppercase tracking-widest">
              {q.questionType}
            </span>
          )}
          {q.topic && (
            <span className="text-xs text-slate-500 bg-slate-900 px-2 py-1 rounded-md border border-slate-800/80">
              Topic: {q.topic}
            </span>
          )}
          {q.customTags && q.customTags.split(',').map(tag => tag.trim()).filter(Boolean).map((tag, idx) => (
            <span key={idx} className="text-xs text-emerald-400/80 bg-emerald-500/5 px-2 py-1 rounded-md border border-emerald-500/10">
              #{tag}
            </span>
          ))}
        </div>
      )}
 
      {q.solution && (
        <div className="mt-6 p-4 rounded-xl bg-slate-950/30 border border-slate-800/50 transition-all duration-300">
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-2 group"
          >
            <div className="flex items-center gap-2">
              <svg 
                className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
                width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="m9 18 6-6-6-6"/>
              </svg>
              <span>Solution / Explanation</span>
            </div>
            <span className="text-[9px] text-slate-500 font-medium lowercase opacity-0 group-hover:opacity-100 transition-opacity">
              {isExpanded ? 'click to hide' : 'click to reveal'}
            </span>
          </button>
          
          {isExpanded && (
            <div 
              className="prose prose-invert max-w-none prose-sm text-slate-400 mt-3 animate-in fade-in slide-in-from-top-1 duration-300"
              dangerouslySetInnerHTML={renderRichText(q.solution)} 
            />
          )}
        </div>
      )}
    </div>
  );
});

// Sub-components
const VideoLectures = () => {
  const [videos, setVideos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({ title: '', subject: '', difficulty: 'Medium' });
  const [file, setFile] = useState(null);

  React.useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = () => {
    axios.get(API_BASE_URL + '/api/files/type/Video')
      .then(res => setVideos(res.data))
      .catch(err => console.error("Error fetching videos:", err));
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      axios.delete(`${API_BASE_URL}/api/files/${id}`)
        .then(() => {
          toast.success("Video deleted successfully");
          fetchVideos();
        })
        .catch(err => {
          console.error(err);
          const serverMessage = err.response?.data?.message || err.message;
          toast.error('Delete failed: ' + serverMessage);
        });
    }
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!formData.title || !file) {
      toast("Please provide a title and select a file.");
      return;
    }
    
    if (file.size > 30 * 1024 * 1024) {
      toast.error("File size cannot exceed 30MB.");
      return;
    }
    
    const validTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    if (file.type && !validTypes.includes(file.type)) {
      toast.error("Invalid file format. Please upload MP4, WebM, or OGG videos.");
      return;
    }
    
    const data = new FormData();
    data.append('file', file);
    data.append('title', formData.title);
    data.append('subject', formData.subject || 'General');
    data.append('type', 'Video');

    axios.post(API_BASE_URL + '/api/files/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then(res => {
      fetchVideos();
      setIsUploading(false);
      setFormData({ title: '', subject: '', difficulty: 'Medium' });
      setFile(null);
    })
    .catch(err => {
      console.error(err);
      const serverMessage = err.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err.message;
      toast.error('Upload failed: ' + serverMessage);
    });
  };

  if (isUploading) {
    return (
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <h3 className="text-xl font-medium text-slate-200 mb-4">Upload Video</h3>
        <form onSubmit={handleUpload} className="space-y-4 max-w-xl">
          <div>
            <input type="file" accept="video/mp4,video/webm,video/ogg" onChange={e => {
              const selectedFile = e.target.files[0];
              if (selectedFile && selectedFile.size > 30 * 1024 * 1024) {
                toast.error("File size cannot exceed 30MB.");
                e.target.value = null;
                setFile(null);
              } else {
                setFile(selectedFile);
              }
            }} className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500/10 file:text-blue-400 hover:file:bg-blue-500/20" required />
            <p className="text-xs text-slate-500 mt-2 ml-1">Max size: 30MB. Allowed formats: MP4, WebM, OGG</p>
          </div>
          <input type="text" placeholder="Video Title" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200" required />
          <input type="text" placeholder="Subject" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200" />
          <div className="flex gap-4 pt-4">
            <button type="button" onClick={() => setIsUploading(false)} className="px-4 py-2 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600">Upload</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {videos.map(video => (
        <div key={video.id} className="group relative rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden hover:border-blue-500/50 transition-all duration-300">
          <div className={`aspect-video w-full bg-blue-500/20 border-b border-slate-800 relative flex items-center justify-center`}>
            <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-transparent transition-colors duration-300"></div>
            <div className="flex gap-3 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(`${API_BASE_URL.startsWith('http') ? API_BASE_URL : window.location.origin + API_BASE_URL}/api/files/download/${video.id}`); toast.success("Link copied!"); }} className="w-12 h-12 rounded-full bg-slate-800/90 hover:bg-slate-700 text-white flex items-center justify-center shadow-xl shadow-slate-900/50" title="Copy Link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
              </button>
              <a href={`${API_BASE_URL}/api/files/download/${video.id}`} download className="w-12 h-12 rounded-full bg-blue-500/90 text-white flex items-center justify-center shadow-xl shadow-blue-500/30" title="Download">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              </a>
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(video.id); }} className="w-12 h-12 rounded-full bg-rose-500/90 hover:bg-rose-600 text-white flex items-center justify-center shadow-xl shadow-rose-500/30" title="Delete Video">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
              </button>
            </div>
            <span className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-slate-950/80 text-xs font-medium text-slate-300 backdrop-blur-sm">
              {video.size}
            </span>
          </div>
          <div className="p-5">
            <div className="flex items-start justify-between mb-2">
              <span className="px-2.5 py-1 rounded-full bg-slate-800 text-xs font-medium text-blue-400 border border-slate-700">
                {video.subject}
              </span>
              <span className="text-xs text-slate-500 font-mono">v1.0</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-200 group-hover:text-blue-400 transition-colors line-clamp-1">{video.title}</h3>
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <div className="truncate">{video.originalFileName}</div>
            </div>
          </div>
        </div>
      ))}
      <button onClick={() => setIsUploading(true)} className="group rounded-2xl border-2 border-dashed border-slate-800 bg-slate-900/20 hover:bg-slate-800/40 hover:border-slate-600 flex flex-col items-center justify-center min-h-[280px] transition-all duration-300">
        <div className="w-14 h-14 rounded-full bg-slate-800 group-hover:bg-blue-500/20 text-slate-400 group-hover:text-blue-400 flex items-center justify-center mb-4 transition-colors">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        </div>
        <span className="text-slate-400 group-hover:text-slate-200 font-medium">Upload Video</span>
      </button>
    </div>
  );
};

const StudyMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({ title: '', subject: '' });
  const [file, setFile] = useState(null);

  React.useEffect(() => { fetchMaterials(); }, []);

  const fetchMaterials = () => {
    axios.get(API_BASE_URL + '/api/files/type/PDF')
      .then(res => setMaterials(res.data))
      .catch(err => console.error("Error fetching materials:", err));
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      axios.delete(`${API_BASE_URL}/api/files/${id}`)
        .then(() => {
          toast.success("Document deleted successfully");
          fetchMaterials();
        })
        .catch(err => {
          console.error(err);
          const serverMessage = err.response?.data?.message || err.message;
          toast.error('Delete failed: ' + serverMessage);
        });
    }
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!formData.title || !file) {
      toast("Please provide a title and select a file.");
      return;
    }
    
    const data = new FormData();
    data.append('file', file);
    data.append('title', formData.title);
    data.append('subject', formData.subject || 'General');
    data.append('type', 'PDF');

    axios.post(API_BASE_URL + '/api/files/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then(res => {
      fetchMaterials();
      setIsUploading(false);
      setFormData({ title: '', subject: '' });
      setFile(null);
    })
    .catch(err => {
      console.error(err);
      const serverMessage = err.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err.message;
      toast.error('Upload failed: ' + serverMessage, { autoClose: false });
    });
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-slate-200">Recent Documents</h3>
        <button onClick={() => setIsUploading(true)} className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          Upload PDF
        </button>
      </div>

      {isUploading && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 mb-6 animate-in fade-in">
          <h3 className="text-xl font-medium text-slate-200 mb-4">Upload Study Material</h3>
          <form onSubmit={handleUpload} className="space-y-4 max-w-xl">
            <input type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])} className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500/10 file:text-blue-400 hover:file:bg-blue-500/20" required />
            <input type="text" placeholder="Document Title" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200" required />
            <input type="text" placeholder="Subject" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200" />
            <div className="flex gap-4 pt-4">
              <button type="button" onClick={() => setIsUploading(false)} className="px-4 py-2 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600">Upload</button>
            </div>
          </form>
        </div>
      )}
      
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 border-b border-slate-800/60 bg-slate-900/60 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <div className="col-span-5">File Name</div>
          <div className="col-span-2">Subject</div>
          <div className="col-span-2">Size</div>
          <div className="col-span-2">Uploaded</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        
        <div className="divide-y divide-slate-800/60">
          {materials.map(doc => (
            <div key={doc.id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-slate-800/40 transition-colors group text-sm">
              <div className="col-span-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                </div>
                <div className="flex flex-col">
                  <span className="font-medium text-slate-200 group-hover:text-blue-400 transition-colors">{doc.title}</span>
                  <span className="text-xs text-slate-500 truncate max-w-[200px]">{doc.originalFileName}</span>
                </div>
              </div>
              <div className="col-span-2 text-slate-400">
                <span className="inline-flex px-2 py-1 rounded bg-slate-800 text-xs text-slate-300">{doc.subject}</span>
              </div>
              <div className="col-span-2 text-slate-500">{doc.size}</div>
              <div className="col-span-2 text-slate-500">
                <div className="flex flex-col">
                  <span className="text-slate-300">{new Date(doc.uploadDate).toLocaleDateString()}</span>
                  <span className="text-xs">{doc.uploadedBy}</span>
                </div>
              </div>
              <div className="col-span-1 flex justify-end gap-2">
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(`${API_BASE_URL.startsWith('http') ? API_BASE_URL : window.location.origin + API_BASE_URL}/api/files/download/${doc.id}`); toast.success("Link copied!"); }} className="w-8 h-8 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 flex items-center justify-center transition-colors" title="Copy Link">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                </button>
                <a href={`${API_BASE_URL}/api/files/download/${doc.id}`} download className="w-8 h-8 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 flex items-center justify-center transition-colors" title="Download">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                </a>
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(doc.id); }} className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-400 hover:text-white hover:bg-rose-500 flex items-center justify-center transition-colors" title="Delete">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const renderRichText = (raw) => {
  if (!raw) return { __html: '' };
  let html = String(raw);
  const cleanMath = (m) => m.replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/<[^>]+>/g, '');
  try {
    // Check for KaTeX before rendering
    if (window.katex) {
      html = html.replace(/\$\$([\s\S]*?)\$\$/g, (match, math) => {
        try { return window.katex.renderToString(cleanMath(math), { displayMode: true, throwOnError: false }); } 
        catch (e) { return match; }
      });
      html = html.replace(/(?<!\$)\$([^$]+)\$(?!\$)/g, (match, math) => {
        try { return window.katex.renderToString(cleanMath(math), { displayMode: false, throwOnError: false }); } 
        catch (e) { return match; }
      });
    }
  } catch (err) {}
  // Force <img> tags to be inline if they are inside <p> or standalone
  html = html.replace(/<img /g, '<img style="display:inline-block; vertical-align:middle; margin:0 4px;" ');
  return { __html: html };
};

  const QuestionBanks = () => {
    const fileInputRef = useRef(null);
    const [questions, setQuestions] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [editingData, setEditingData] = useState(null); // The question object currently being edited in modal
    const [filters, setFilters] = useState({ subject: '', chapter: '', difficulty: '', questionType: '', customTags: '' });
    const [chaptersList, setChaptersList] = useState([]);
    const [subjectsList, setSubjectsList] = useState([]);
    const [difficultiesList, setDifficultiesList] = useState([]);
    const [questionTypesList, setQuestionTypesList] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [activeEditor, setActiveEditor] = useState(null); 
  const quillRefs = useRef({}); 

  const applyValueToEditorState = useCallback((editorId, newValue) => {
    if (editorId.startsWith('create_text')) {
      setFormData(prev => ({ ...prev, text: newValue }));
    }
    else if (editorId.startsWith('create_solution')) {
      setFormData(prev => ({ ...prev, solution: newValue }));
    }
    else if (editorId.startsWith('create_opt_')) {
      const idx = parseInt(editorId.split('_').pop(), 10);
      setFormData(prev => {
        const newOptions = [...prev.options];
        newOptions[idx] = newValue;
        return { ...prev, options: newOptions };
      });
    }
    else if (editorId.startsWith('edit_text_')) {
      setEditingData(prev => prev ? { ...prev, text: newValue } : prev);
    }
    else if (editorId.startsWith('edit_solution_')) {
      setEditingData(prev => prev ? { ...prev, solution: newValue } : prev);
    }
    else if (editorId.startsWith('edit_opt_')) {
      const idx = parseInt(editorId.split('_').pop(), 10);
      setEditingData(prev => {
        if (!prev) return prev;
        const newOptions = [...prev.options];
        newOptions[idx] = newValue;
        return { ...prev, options: newOptions };
      });
    }
  }, []);

  const insertSymbol = useCallback((symbol) => {
    if (!activeEditor) return;
    const el = quillRefs.current[activeEditor.id];
    if (!el) return;
    const editorId = activeEditor.id;

    if (el.isContentEditable) {
      el.focus();
      const selection = window.getSelection();
      let range = null;

      if (selection && selection.rangeCount > 0 && el.contains(selection.anchorNode)) {
        range = selection.getRangeAt(0);
      } else {
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
      }

      range.deleteContents();
      const textNode = document.createTextNode(symbol);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }

      applyValueToEditorState(editorId, el.innerHTML);
      return;
    }

    // Handle standard textarea/input
    if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const text = el.value;
      const newValue = text.substring(0, start) + symbol + text.substring(end);
      applyValueToEditorState(editorId, newValue);

      // Restore focus and cursor
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + symbol.length, start + symbol.length);
      }, 0);
    } 
    // Handle Quill fallback
    else if (el.getEditor) {
      const quill = el.getEditor();
      const range = quill.getSelection(true);
      quill.insertText(range.index, symbol);
      quill.setSelection(range.index + 1);
    }
  }, [activeEditor, applyValueToEditorState]);


  const startEditing = useCallback((q) => {
    setEditingData({ ...q });
  }, []);

  const handleEditOptionChange = useCallback((index, value) => {
    setEditingData(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return { ...prev, options: newOptions };
    });
  }, []);

  const toggleCorrectOption = (letter, isEdit = false) => {
    const setter = isEdit ? setEditingData : setFormData;
    setter(prev => {
      let current = String(prev.correctOption || '');
      if (current.includes(letter)) {
        current = current.replace(letter, '');
      } else {
        current += letter;
      }
      // Sort alphabetically
      const sorted = current.split('').sort().join('');
      return { ...prev, correctOption: sorted };
    });
  };

  const submitEdit = (e) => {
    if (!editingData.text.trim()) {
      toast("Please fill question content.");
      return;
    }
    const isInteger = editingData.questionType === 'Integer Type';
    const isMultiple = editingData.questionType === 'Multiple Correct';
    if (!isInteger && !isMultiple && editingData.options.some(opt => !String(opt).trim())) {
      toast("Please fill all options.");
      return;
    }
    if ((isInteger || isMultiple) && (!editingData.correctOption || !String(editingData.correctOption).trim())) {
      toast("Please enter the correct answer.");
      return;
    }

    axios.put(API_BASE_URL + '/api/questions/' + editingData.id, editingData)
      .then(() => {
        fetchQuestions();
        setEditingData(null);
        toast.success("Question updated!");
      })
      .catch(err => {
        console.error("Error updating question:", err);
        toast("Error updating database.");
      });
  };

  React.useEffect(() => {
    fetchQuestions(0);
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    axios
      .get(API_BASE_URL + "/api/questions/metadata/subjects")
      .then((res) => setSubjectsList(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching subjects:", err));

    axios
      .get(API_BASE_URL + "/api/questions/metadata/difficulties")
      .then((res) => setDifficultiesList(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching difficulties:", err));

    axios
      .get(API_BASE_URL + "/api/questions/metadata/question-types")
      .then((res) => setQuestionTypesList(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching question types:", err));
  }, []);

  // Fetch unique chapters when subject changes
  React.useEffect(() => {
    if (filters.subject) {
      axios.get(`${API_BASE_URL}/api/questions/metadata/chapters`, { params: { subject: filters.subject } })
        .then(res => setChaptersList(res.data))
        .catch(err => console.error("Error fetching chapters:", err));
    } else {
      setChaptersList([]);
      setFilters(prev => ({ ...prev, chapter: '' }));
    }
  }, [filters.subject]);


  const fetchQuestions = useCallback((page = currentPage) => {
    setIsLoading(true);
    axios.get(API_BASE_URL + '/api/questions', { params: { ...filters, page, size: 200 } })
      .then(res => {
        const mapped = res.data.map(q => {
          const isInteger = q.questionType === 'Integer Type';
          const isMultiple = q.questionType === 'Multiple Correct';
          return {
            ...q,
            options: [q.optionA, q.optionB, q.optionC, q.optionD],
            correctOptionIndex: (!isInteger && !isMultiple && q.correctOption && q.correctOption.length === 1) 
              ? q.correctOption.charCodeAt(0) - 65 
              : 0
          };
        });
        // Backend order is generally oldest first, let's reverse to show newest first
        setQuestions(mapped.reverse());
        setCurrentPage(page);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Error fetching questions:", err);
        setIsLoading(false);
      });
    
    // Fetch filtered count
    axios.get(API_BASE_URL + '/api/questions/count', { params: { ...filters } })
      .then(res => setTotalCount(res.data))
      .catch(err => console.error("Error fetching count:", err));
  }, [filters, currentPage]);


  const [formData, setFormData] = useState({
    text: '',
    options: ['', '', '', ''],
    correctOptionIndex: 0,
    subject: '',
    chapter: '',
    difficulty: 'Medium',
    questionType: 'Multiple Choice Single Correct',
    correctOption: '',
    customTags: '',
    targetClass: '',
    solution: ''
  });

  const handleOptionChange = useCallback((index, value) => {
    setFormData(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return { ...prev, options: newOptions };
    });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const isInteger = formData.questionType === 'Integer Type';
    const isMultiple = formData.questionType === 'Multiple Correct';
    if (!formData.text.trim()) {
      toast("Please enter question text.");
      return;
    }
    if (!isInteger && !isMultiple && formData.options.some(opt => !opt.trim())) {
      toast("Please fill all 4 options.");
      return;
    }
    if ((isInteger || isMultiple) && !formData.correctOption.trim()) {
      toast("Please enter the correct answer.");
      return;
    }

    const newQuestion = {
      ...formData,
      id: Date.now(),
      date: new Date().toISOString()
    };
    
    // Save to DB via bulk endpoint (even for a single question)
    axios.post(API_BASE_URL + '/api/questions/bulk', [newQuestion])
      .then(() => {
        fetchQuestions();
        setIsCreating(false);
        setFormData({
          text: '',
          options: ['', '', '', ''],
          correctOptionIndex: 0,
          subject: '',
          chapter: '',
          topic: '',
          difficulty: 'Medium',
          questionType: 'Single Correct',
          correctOption: 'A',
          customTags: '',
          solution: ''
        });
  })
  .catch(err => {
    console.error("Error saving manual question:", err);
    toast("Error saving. DB might be down.");
  });
};

  const removeQuestion = useCallback((id) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      axios.delete(`${API_BASE_URL}/api/questions/${id}`)
        .then(() => {
          setQuestions(prev => prev.filter(q => q.id !== id));
          toast.success("Question deleted successfully");
        })
        .catch(err => {
          console.error("Delete error:", err);
          toast.error("Failed to delete question");
        });
    }
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        
        const newQuestions = data.map((row, idx) => ({
          id: Date.now() + idx,
          text: row['Question'] || '',
          options: [
            row['Option A'] || '',
            row['Option B'] || '',
            row['Option C'] || '',
            row['Option D'] || ''
          ],
          correctOption: String(row['Correct Option'] || ''),
          correctOptionIndex: (row['Correct Option'] && String(row['Correct Option']).trim().length === 1 && /^[A-D]$/i.test(String(row['Correct Option']).trim())) 
            ? String(row['Correct Option']).trim().toUpperCase().charCodeAt(0) - 65 
            : 0,
          subject: row['Subject'] || 'N/A',
          chapter: row['Chapter'] || '',
          topic: row['Topic'] || '',
          difficulty: row['Difficulty'] || 'Medium',
          customTags: row['Custom Tags'] || '',
          solution: row['Solution'] || '',
          questionType: row['Question Type'] || 'Single Correct',
          targetClass: row['Target Class'] || '',
          date: new Date().toISOString()
        }));

        setQuestions(prev => [...newQuestions, ...prev]);
        
        axios.post(API_BASE_URL + '/api/questions/bulk', newQuestions)
          .then(res => {
            toast(`Successfully imported and saved ${newQuestions.length} questions to the database!`);
            fetchQuestions();
          })
          .catch(err => {
            console.error('API Error:', err);
            toast(`Questions loaded in UI, but failed to save to database. Error: ${err.message}`);
          });
      } catch (error) {
        console.error(error);
        toast('Error parsing the Excel file. Please ensure it follows the sample format.');
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = null; // reset
  };

  if (isCreating) {
    return (
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between mb-6 border-b border-slate-800 pb-4">
          <h3 className="text-xl font-medium text-slate-200">Create New MCQ</h3>
          <button 
            onClick={() => setIsCreating(false)}
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Cancel
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Question Text */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Question Text</label>
            <div className="flex flex-col">
              <SymbolPicker onSelect={insertSymbol} />
              <div className="bg-slate-950/50 border border-slate-800 rounded-b-xl overflow-hidden">
                <textarea 
                  ref={(el) => { if (el) quillRefs.current['create_text'] = el; }}
                  onFocus={() => setActiveEditor({ id: 'create_text' })}
                  value={formData.text}
                  onChange={(e) => setFormData({...formData, text: e.target.value})}
                  className="w-full bg-transparent p-4 min-h-[120px] text-slate-200 outline-none resize-y"
                  placeholder="Enter question content (HTML supported)..."
                />
              </div>
            </div>
          </div>

          {/* Solution Text */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Solution / Explanation</label>
            <div className="flex flex-col">
              <SymbolPicker onSelect={insertSymbol} />
              <div className="bg-slate-950/50 border border-slate-800 rounded-b-xl overflow-hidden">
                <textarea 
                  ref={(el) => { if (el) quillRefs.current['create_solution'] = el; }}
                  onFocus={() => setActiveEditor({ id: 'create_solution' })}
                  value={formData.solution}
                  onChange={(e) => setFormData({...formData, solution: e.target.value})}
                  className="w-full bg-transparent p-4 min-h-[120px] text-slate-200 outline-none resize-y"
                  placeholder="Explain the answer (optional)..."
                />
              </div>
            </div>
          </div>

          {/* Tagging Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4 border-t border-slate-800">
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Question Type</label>
              <select 
                value={formData.questionType}
                onChange={(e) => setFormData({...formData, questionType: e.target.value, correctOption: ''})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
              >
                <option value="Single Correct">Single Correct</option>
                <option value="Multiple Correct">Multiple Correct</option>
                <option value="Integer Type">Integer Type</option>
                <option value="Assertion Reason">Assertion Reason</option>
              </select>
            </div>
          </div>

          {/* Options Section */}
          <div className="space-y-4 pt-4 border-t border-slate-800">
            {formData.questionType === 'Integer Type' ? (
              <div className="space-y-2 max-w-xs">
                <label className="text-sm font-medium text-slate-300">Integer Answer</label>
                <input 
                  type="text"
                  maxLength={10}
                  value={formData.correctOption}
                  onChange={(e) => setFormData({...formData, correctOption: e.target.value})}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-emerald-400 font-mono font-bold text-lg focus:border-emerald-500 outline-none transition-all placeholder:text-slate-700"
                  placeholder="Enter integer..."
                />
                <p className="text-[10px] text-slate-500 italic">This will be stored as the correct answer (max 10 chars).</p>
              </div>
            ) : formData.questionType === 'Multiple Correct' ? (
              <>
                <label className="text-sm font-medium text-slate-300">Options & Correct Answers (Multiple Selection)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.options.map((opt, idx) => (
                    <div key={idx} className="flex flex-col bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden focus-within:border-blue-500 transition-colors">
                      <div className="flex items-center gap-3 p-2 border-b border-slate-800">
                        <input 
                          type="checkbox" 
                          checked={String(formData.correctOption || '').includes(String.fromCharCode(65 + idx))}
                          onChange={() => toggleCorrectOption(String.fromCharCode(65 + idx))}
                          className="w-4 h-4 ml-2 accent-emerald-500 cursor-pointer"
                        />
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Option {String.fromCharCode(65 + idx)}</span>
                      </div>
                      <SymbolPicker onSelect={insertSymbol} />
                      <div className="flex-1 min-h-[60px]">
                        <textarea 
                          ref={(el) => { if (el) quillRefs.current[`create_opt_${idx}`] = el; }}
                          onFocus={() => setActiveEditor({ id: `create_opt_${idx}` })}
                          value={opt}
                          onChange={(e) => handleOptionChange(idx, e.target.value)}
                          className="w-full bg-transparent p-3 text-sm text-slate-200 outline-none resize-none min-h-[80px]"
                          placeholder={`Type option content...`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 italic">Multi-correct answer: {formData.correctOption || '(none selected)'}</p>
              </>
            ) : (
              <>
                <label className="text-sm font-medium text-slate-300">Options & Correct Answer</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.options.map((opt, idx) => (
                    <div key={idx} className="flex flex-col bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden focus-within:border-blue-500 transition-colors">
                      <div className="flex items-center gap-3 p-2 border-b border-slate-800">
                        <input 
                          type="radio" 
                          name="correctOption" 
                          checked={formData.correctOptionIndex === idx}
                          onChange={() => setFormData({...formData, correctOptionIndex: idx, correctOption: String.fromCharCode(65 + idx)})}
                          className="w-4 h-4 ml-2 accent-blue-500"
                        />
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Option {String.fromCharCode(65 + idx)}</span>
                      </div>
                      <SymbolPicker onSelect={insertSymbol} />
                      <div className="flex-1 min-h-[60px]">
                        <textarea 
                          ref={(el) => { if (el) quillRefs.current[`create_opt_${idx}`] = el; }}
                          onFocus={() => setActiveEditor({ id: `create_opt_${idx}` })}
                          value={opt}
                          onChange={(e) => handleOptionChange(idx, e.target.value)}
                          className="w-full bg-transparent p-3 text-sm text-slate-200 outline-none resize-none min-h-[80px]"
                          placeholder={`Type option content...`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Select the radio button next to the correct option.</p>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4 border-t border-slate-800">
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Subject</label>
              <input 
                type="text" 
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. Physics"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Chapter</label>
              <input 
                type="text" 
                value={formData.chapter}
                onChange={(e) => setFormData({...formData, chapter: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. Thermodynamics"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Topic</label>
              <input 
                type="text" 
                value={formData.topic}
                onChange={(e) => setFormData({...formData, topic: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. Entropy"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Target Class / Grade</label>
              <input 
                type="text" 
                value={formData.targetClass}
                onChange={(e) => setFormData({...formData, targetClass: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. Class 12, NEET Batch"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Difficulty</label>
              <select 
                value={formData.difficulty}
                onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
            <div className="space-y-2 xl:col-span-2">
              <label className="text-xs font-medium text-slate-400">Custom Tags (comma separated)</label>
              <input 
                type="text" 
                value={formData.customTags}
                onChange={(e) => setFormData({...formData, customTags: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. previous-year, important"
              />
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-slate-800 gap-4">
            <button 
              type="button"
              onClick={() => setIsCreating(false)}
              className="px-6 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-6 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium shadow-lg shadow-blue-500/20 transition-colors"
            >
              Save Question
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-slate-200 flex items-center gap-2">
            Question Bank
            <span className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full border border-blue-500/20 uppercase tracking-wider">
              {isLoading ? 'Syncing...' : `${totalCount.toLocaleString()} Total`}
            </span>
          </h3>
          <p className="text-sm text-slate-500 mt-1">Manage MCQs and adaptative question sets.</p>
        </div>
        <div className="flex gap-3">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
            accept=".xlsx, .xls, .csv" 
            className="hidden" 
          />
          <button 
            onClick={() => setIsBulkModalOpen(true)}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition-colors border border-slate-700 flex items-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
            Bulk Upload
          </button>
          <button 
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add MCQ
          </button>
        </div>
      </div>

      {/* Filters UI */}
      <div className="flex flex-col sm:flex-row sm:items-center p-4 bg-slate-900/40 border border-slate-800 rounded-2xl gap-4">
        <div className="flex items-center gap-2 text-slate-400">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
          <span className="text-sm font-medium">Filters:</span>
        </div>
        <div className="flex flex-wrap gap-3 flex-1">
          <select
            value={filters.subject}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                subject: e.target.value,
                chapter: "",
              }))
            }
            className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-[220px] max-w-[220px]"
          >
            <option value="">All Subjects</option>
            {subjectsList.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>

          {filters.subject && (
            <select
              value={filters.chapter}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, chapter: e.target.value }))
              }
              className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-[220px] max-w-[220px] animate-in fade-in slide-in-from-left-2"
            >
              <option value="">All Chapters</option>
              {chaptersList.map(ch => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
          )}

          <select
            value={filters.difficulty}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, difficulty: e.target.value }))
            }
            className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-[190px] max-w-[190px]"
          >
            <option value="">All Difficulties</option>
            {difficultiesList.map((difficulty) => (
              <option key={difficulty} value={difficulty}>
                {difficulty}
              </option>
            ))}
          </select>

          <select
            value={filters.questionType}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, questionType: e.target.value }))
            }
            className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-[220px] max-w-[220px]"
          >
            <option value="">All Question Types</option>
            {questionTypesList.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Custom Tag (e.g. important)"
            value={filters.customTags}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, customTags: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && fetchQuestions()}
            className="bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 flex-1 min-w-[200px]"
          />
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => fetchQuestions(0)} 
            disabled={isLoading}
            className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 text-white text-sm font-medium transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2"
          >
            {isLoading && <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>}
            {isLoading ? 'Loading...' : 'Show Questions'}
          </button>
          { (filters.subject || filters.chapter || filters.difficulty || filters.questionType || filters.customTags) && (
            <button onClick={() => {
              setFilters({subject: '', chapter: '', difficulty: '', questionType: '', customTags: ''});

              // Give state a millisecond to update, then fetch all
              setTimeout(() => {
                axios.get(API_BASE_URL + '/api/questions', { params: { subject: '', chapter: '', difficulty: '', questionType: '', customTags: '', page: 0, size: 200 } })
                  .then(res => {
                    const mapped = res.data.map(q => ({
                      ...q,
                      options: [q.optionA, q.optionB, q.optionC, q.optionD],
                      correctOptionIndex: q.correctOption ? q.correctOption.charCodeAt(0) - 65 : 0
                    }));
                    setQuestions(mapped.reverse());
                    setCurrentPage(0);
                  })
                  .catch(console.error);
              }, 0);
            }} className="text-xs text-rose-400 hover:text-rose-300 px-3 py-2 rounded-xl border border-rose-500/20 bg-rose-500/10 transition-colors">
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-slate-500">
          Showing <span className="text-slate-300 font-bold">{questions.length}</span> questions of <span className="text-slate-300 font-bold">{totalCount.toLocaleString()}</span> 
          {currentPage > 0 && <span> (Page {currentPage + 1})</span>}
        </div>
        <div className="flex items-center gap-3">
          <button 
            disabled={currentPage === 0}
            onClick={() => fetchQuestions(currentPage - 1)}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-200 text-xs font-bold border border-slate-700 transition-all flex items-center gap-2"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            Previous 200
          </button>
          <button 
            disabled={questions.length < 200}
            onClick={() => fetchQuestions(currentPage + 1)}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-200 text-xs font-bold border border-slate-700 transition-all flex items-center gap-2"
          >
            Next 200
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>
      </div>

      <div className="relative min-h-[400px]">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-slate-950/40 backdrop-blur-[2px] rounded-3xl border border-slate-800/50">
            <div className="w-10 h-10 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin mb-4"></div>
            <p className="text-slate-400 text-sm font-medium animate-pulse">Syncing with Question Vault...</p>
          </div>
        )}

        {questions.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] border-2 border-dashed border-slate-800 rounded-3xl bg-slate-900/20">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center text-slate-500 mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
            </div>
            <p className="text-slate-400 font-medium tracking-wide">No questions found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {questions.map((q) => (
              <QuestionItem 
                key={q.id} 
                q={q} 
                onEdit={startEditing} 
                onDelete={removeQuestion} 
                renderRichText={renderRichText} 
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Question Modal */}
      {editingData && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md" onClick={() => setEditingData(null)}></div>
          <div className="relative bg-slate-900 border border-slate-700 rounded-3xl p-6 md:p-8 w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200 custom-scrollbar">
            <div className="flex items-center justify-between mb-6 border-b border-slate-800/60 pb-4">
              <h3 className="text-2xl font-bold text-white">Edit Question</h3>
              <button onClick={() => setEditingData(null)} className="text-slate-500 hover:text-white transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <form onSubmit={submitEdit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-400">Question Content</label>
                <div className="flex flex-col">
                  <SymbolPicker onSelect={insertSymbol} />
                  <div className="bg-slate-950/50 border border-slate-800 rounded-b-xl overflow-hidden">
                    <HtmlEditor
                      editorId={`edit_text_${editingData.id}`}
                      registerRef={(el) => { if (el) quillRefs.current[`edit_text_${editingData.id}`] = el; }}
                      onFocus={() => setActiveEditor({ id: `edit_text_${editingData.id}` })}
                      value={editingData.text}
                      onChange={(value) => setEditingData({...editingData, text: value})}
                      placeholder="Edit question content..."
                      minHeightClass="min-h-[150px]"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-400">Solution / Explanation</label>
                <div className="flex flex-col">
                  <SymbolPicker onSelect={insertSymbol} />
                  <div className="bg-slate-950/50 border border-slate-800 rounded-b-xl overflow-hidden">
                    <HtmlEditor
                      editorId={`edit_solution_${editingData.id}`}
                      registerRef={(el) => { if (el) quillRefs.current[`edit_solution_${editingData.id}`] = el; }}
                      onFocus={() => setActiveEditor({ id: `edit_solution_${editingData.id}` })}
                      value={editingData.solution || ''}
                      onChange={(value) => setEditingData({...editingData, solution: value})}
                      placeholder="Edit solution..."
                      minHeightClass="min-h-[120px]"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-800">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase ml-1">Question Type</label>
                  <select 
                    value={editingData.questionType}
                    onChange={(e) => setEditingData({...editingData, questionType: e.target.value, correctOption: ''})}
                    className="w-full max-w-xs bg-slate-950/50 border border-slate-800 rounded-xl p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors"
                  >
                    <option value="Single Correct">Single Correct</option>
                    <option value="Multiple Correct">Multiple Correct</option>
                    <option value="Integer Type">Integer Type</option>
                    <option value="Assertion Reason">Assertion Reason</option>
                  </select>
                </div>

                {editingData.questionType === 'Integer Type' ? (
                  <div className="space-y-2 max-w-xs pt-2">
                    <label className="text-sm font-semibold text-slate-400">Integer Answer</label>
                    <input 
                      type="text"
                      maxLength={10}
                      value={editingData.correctOption}
                      onChange={(e) => setEditingData({...editingData, correctOption: e.target.value})}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-emerald-400 font-mono font-bold text-lg focus:border-emerald-500 outline-none transition-all"
                      placeholder="Enter integer..."
                    />
                    <p className="text-[10px] text-slate-500 italic">Correct answer for this integer question.</p>
                  </div>
                ) : editingData.questionType === 'Multiple Correct' ? (
                  <>
                    <label className="text-sm font-semibold text-slate-400">Options & Correct Answers (Multiple)</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {editingData.options.map((opt, idx) => (
                        <div key={idx} className="flex flex-col bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden focus-within:border-blue-500 transition-all">
                          <div className="flex items-center gap-3 p-2 border-b border-slate-800 bg-slate-900/40">
                            <input 
                              type="checkbox" 
                              checked={String(editingData.correctOption || '').includes(String.fromCharCode(65 + idx))}
                              onChange={() => toggleCorrectOption(String.fromCharCode(65 + idx), true)}
                              className="w-4 h-4 ml-2 accent-emerald-500 cursor-pointer"
                            />
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Option {String.fromCharCode(65 + idx)}</span>
                          </div>
                          <SymbolPicker onSelect={insertSymbol} />
                          <div className="flex-1 min-h-[60px]">
                            <HtmlEditor
                              editorId={`edit_opt_${editingData.id}_${idx}`}
                              registerRef={(el) => { if (el) quillRefs.current[`edit_opt_${editingData.id}_${idx}`] = el; }}
                              onFocus={() => setActiveEditor({ id: `edit_opt_${editingData.id}_${idx}` })}
                              value={opt}
                              onChange={(value) => handleEditOptionChange(idx, value)}
                              placeholder={`Type option content...`}
                              minHeightClass="min-h-[80px]"
                              paddingClass="p-3"
                              textClassName="text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {editingData.options.map((opt, idx) => (
                      <div key={idx} className="flex flex-col bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden focus-within:border-blue-500 transition-all">
                        <div className="flex items-center gap-3 p-2 border-b border-slate-800 bg-slate-900/40">
                          <input 
                            type="radio" 
                            name="modalCorrectOption" 
                            checked={editingData.correctOptionIndex === idx} 
                            onChange={() => setEditingData({...editingData, correctOptionIndex: idx, correctOption: String.fromCharCode(65 + idx)})} 
                            className="w-4 h-4 ml-2 accent-blue-500 cursor-pointer" 
                          />
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Option {String.fromCharCode(65 + idx)}</span>
                        </div>
                        <SymbolPicker onSelect={insertSymbol} />
                        <div className="flex-1 min-h-[60px]">
                          <HtmlEditor
                            editorId={`edit_opt_${editingData.id}_${idx}`}
                            registerRef={(el) => { if (el) quillRefs.current[`edit_opt_${editingData.id}_${idx}`] = el; }}
                            onFocus={() => setActiveEditor({ id: `edit_opt_${editingData.id}_${idx}` })}
                            value={opt}
                            onChange={(value) => handleEditOptionChange(idx, value)}
                            placeholder={`Type option content...`}
                            minHeightClass="min-h-[80px]"
                            paddingClass="p-3"
                            textClassName="text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-slate-800/60">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Subject</span>
                  <input type="text" placeholder="Subject" value={editingData.subject} onChange={e => setEditingData({...editingData, subject: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Chapter</span>
                  <input type="text" placeholder="Chapter" value={editingData.chapter} onChange={e => setEditingData({...editingData, chapter: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Topic</span>
                  <input type="text" placeholder="Topic" value={editingData.topic} onChange={e => setEditingData({...editingData, topic: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Target Class</span>
                  <input type="text" placeholder="Class / Grade" value={editingData.targetClass} onChange={e => setEditingData({...editingData, targetClass: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Difficulty</span>
                  <select value={editingData.difficulty} onChange={e => setEditingData({...editingData, difficulty: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors">
                    <option value="Easy">Easy</option><option value="Medium">Medium</option><option value="Hard">Hard</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end pt-6 gap-4 border-t border-slate-800/60">
                <button type="button" onClick={() => setEditingData(null)} className="px-6 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm font-medium transition-colors">Cancel</button>
                <button type="submit" className="px-8 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold shadow-xl shadow-blue-500/20 transition-all active:scale-[0.98]">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {isBulkModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 text-left">
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setIsBulkModalOpen(false)}></div>
          <div className="relative bg-slate-900 border border-slate-800 rounded-3xl p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <button 
              onClick={() => setIsBulkModalOpen(false)}
              className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-blue-500/20 text-blue-400 flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-100">Bulk Upload MCQ</h3>
              <p className="text-slate-400 mt-2">Upload your questions using our Excel template for quick importing.</p>
            </div>
            
            <div className="space-y-6">
              <div className="p-4 rounded-2xl bg-slate-950/50 border border-slate-800">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Instructions</h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span> Ensure all columns match the sample format.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span> Format correct answers as 'A', 'B', 'C', or 'D'.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span> Use <code>$$ math $$</code> for mathematical formulas.
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-3">
                <a 
                  href="/sample_mcqs.xlsx" 
                  download 
                  className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium flex items-center justify-center gap-2 transition-all border border-slate-700 hover:border-slate-500 group"
                >
                  <svg className="text-green-500 group-hover:scale-110 transition-transform" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  Download Sample Excel
                </a>
                
                <button 
                  onClick={() => {
                    fileInputRef.current.click();
                    setIsBulkModalOpen(false);
                  }}
                  className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold flex items-center justify-center gap-3 transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98]"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                  Select File to Upload
                </button>
              </div>
            </div>
            
            <p className="text-[10px] text-center text-slate-600 mt-6 uppercase tracking-[0.2em] font-bold">Supported formats: .xlsx, .xls, .csv</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CMS() {
  const [activeTab, setActiveTab] = useState('questions');
  const [counts, setCounts] = useState({ videos: 0, materials: 0, questions: 0 });

  React.useEffect(() => {
    Promise.all([
      axios.get(API_BASE_URL + '/api/files/type/Video').catch(() => ({ data: [] })),
      axios.get(API_BASE_URL + '/api/files/type/PDF').catch(() => ({ data: [] })),
      axios.get(API_BASE_URL + '/api/questions/count').catch(() => ({ data: 0 }))
    ]).then(([vidRes, matRes, qCountRes]) => {
      setCounts({
        videos: vidRes.data ? vidRes.data.length : 0,
        materials: matRes.data ? matRes.data.length : 0,
        questions: qCountRes.data || 0
      });
    });
  }, []);

  const tabs = [
    { id: 'questions', label: 'Question Banks', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg> },
    { id: 'videos', label: 'Video Lectures', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg> },
    { id: 'materials', label: 'Study Material', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg> },
  ];

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12">
      <style>{inlineImageStyle}</style>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
            Content Management System
          </h1>
          <p className="mt-2 text-lg text-slate-400 max-w-2xl">
            Upload and organize video lectures, study materials, and assessments. Features versioning and chapter tagging.
          </p>
        </div>
        
        {/* Quick Stats */}
        <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-xl border border-slate-800">
          <div className="px-4 py-2 text-center border-r border-slate-700/50">
            <span className="block text-2xl font-bold text-white">{counts.videos}</span>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Videos</span>
          </div>
          <div className="px-4 py-2 text-center border-r border-slate-700/50">
            <span className="block text-2xl font-bold text-white">{counts.materials}</span>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Notes</span>
          </div>
          <div className="px-4 py-2 text-center">
            <span className="block text-2xl font-bold text-white">{counts.questions}</span>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">MCQs</span>
          </div>
        </div>
      </div>
      
      {/* Navigation Tabs */}
      <div className="border-b border-slate-800">
        <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-none" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-all duration-300
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-700'
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="pt-2">
        {activeTab === 'videos' && <VideoLectures />}
        {activeTab === 'materials' && <StudyMaterials />}
        {activeTab === 'questions' && <QuestionBanks />}
      </div>
    </div>
  );
}
